import {
  Badge,
  Box,
  Center,
  Flex,
  Grid,
  GridItem,
  Spinner,
  Stack,
  Text,
  Tooltip,
} from '@chakra-ui/react';

import { InfoCircleOutline } from '@aslanpay/portal-common-lib';

import { Status } from 'app/constants/individual';
import useFetchEmployeesStats from 'app/hooks/useFetchEmployeesStats';

import { EmployeesCountByStatus, EmployeesStatusProps } from './types';

function EmployeesStatus({ company }: EmployeesStatusProps) {
  const { data: employeesStats, isLoading: isLoadingEmployeesStats } = useFetchEmployeesStats([
    company?.id || '',
  ]);

  return (
    <Flex flexDir="column" gap="20px">
      <Text as="h5" textStyle="h5SemiBold" color="brand.darkGray">
        Employees status
      </Text>
      {isLoadingEmployeesStats ? (
        <Center width="full" height="full" flexGrow={1}>
          <Spinner color="brand.purple" size="xl" mb="50px" />
        </Center>
      ) : (
        <>
          <Flex
            px="20px"
            py="10px"
            gap="60px"
            border="1px solid"
            borderRadius="10px"
            borderColor="brand.lightGray1"
          >
            <Stack spacing="2px">
              <Text textStyle="smallMedium" color="brand.darkGray">
                Current employees
              </Text>
              <Text as="h3" textStyle="h3Medium" color="brand.black">
                {employeesStats?.currentEmployeesCount}
              </Text>
            </Stack>
            <Stack spacing="2px">
              <Text textStyle="smallMedium" color="brand.darkGray">
                Total employees
              </Text>
              <Text as="h3" textStyle="h3Medium" color="brand.black">
                {employeesStats?.total}
              </Text>
            </Stack>
          </Flex>
          <Grid
            templateColumns={{ base: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)', lg: 'repeat(4, 1fr)' }}
            columnGap={{ base: '20px', md: '40px', xl: '60px' }}
            rowGap="30px"
          >
            {Object.entries(Status)
              .filter((entry) => entry[0] !== 'DELETED')
              .map(([status, props]) => {
                const statValue =
                  employeesStats?.employeesCountByStatus[status as keyof EmployeesCountByStatus] ||
                  0;
                const total = employeesStats?.total || 0;

                return (
                  <GridItem key={status}>
                    <Flex flexGrow={1} flexDirection="column">
                      <Badge size="lg" colorScheme={`badge.${props.color}`}>
                        {props.value}
                        <Tooltip
                          hasArrow
                          shouldWrapChildren
                          arrowPadding={0}
                          placement="top-end"
                          label={props.tooltip}
                        >
                          <InfoCircleOutline boxSize="20px" cursor="pointer" />
                        </Tooltip>
                      </Badge>
                      <Box
                        px="20px"
                        py="23px"
                        flexGrow={1}
                        borderWidth="0 1px 1px"
                        borderColor="brand.lightGray1"
                        borderBottomRadius="5px"
                      >
                        <Text textStyle="h3Medium" color="brand.mutedPurple">
                          {Math.round((statValue / total) * 100) || 0}%{' '}
                          <Text as="span" color="brand.darkGray" textStyle="h5SemiBold">
                            / {statValue}
                          </Text>
                        </Text>
                      </Box>
                    </Flex>
                  </GridItem>
                );
              })}
          </Grid>
        </>
      )}
    </Flex>
  );
}

export default EmployeesStatus;
